import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { RestoreWipedPlayersCard } from './RestoreWipedPlayersCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const REQUIRED_PHRASE = 'WIPE TEST DATA';
const TP_REQUIRED_PHRASE = 'WIPE';

/**
 * Surgical "wipe test players only" card — zeroes balances + deletes the
 * transaction history of test-name/email-pattern accounts but leaves real
 * customers untouched. Silent (no audit log) per operator preference.
 *
 * Two-step flow: GET /preview shows the count + dollars about to be wiped,
 * then POST /wipe with `confirm: "WIPE"` actually does it.
 */
const WipeTestPlayersCard = () => {
    const [open, setOpen] = useState(false);
    const [phrase, setPhrase] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const loadPreview = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/test-players/preview`, { withCredentials: true });
            setPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load preview');
        }
    };

    const openModal = async () => {
        setOpen(true); setResult(null); setPhrase(''); setPreview(null);
        await loadPreview();
    };

    const doWipe = async () => {
        if (phrase.trim() !== TP_REQUIRED_PHRASE) {
            toast.error(`Type exactly: ${TP_REQUIRED_PHRASE}`); return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/test-players/wipe`,
                { confirm: 'WIPE' }, { withCredentials: true });
            setResult(data);
            toast.success(`Wiped ${data.wiped_count} test players — $${data.credits_zeroed.toFixed(2)} zeroed`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Wipe failed');
        }
        setLoading(false);
    };

    const close = (o) => {
        if (loading) return;
        setOpen(o);
        if (!o) { setPhrase(''); setPreview(null); setResult(null); }
    };

    const phraseValid = phrase.trim() === TP_REQUIRED_PHRASE;
    const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="rounded-lg border border-[#FFD700]/30 bg-[#0a0a0a] p-4 mt-3">
            <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-[#FFD700] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">Wipe Test Players Only</p>
                    <p className="text-[11px] text-[#A0AAB2] mt-1">
                        Surgically zeroes the <span className="font-bold text-[#FFD700]">balances + transaction history</span> of every account whose name or email matches the test-player heuristic
                        (<span className="font-mono">&quot;Game User&quot;</span>, <span className="font-mono">&quot;Demo&quot;</span>, <span className="font-mono">&quot;Test&quot;</span>, <span className="font-mono">@test.com</span>, <span className="font-mono">@slotgame.com</span>, <span className="font-mono">@betohalics.app</span>, etc).
                        <strong className="text-white"> Real customer accounts are not touched.</strong> Silent — no audit log.
                    </p>
                </div>
            </div>
            <Button
                onClick={openModal}
                className="bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-bold w-full sm:w-auto"
                data-testid="open-wipe-test-players-btn"
            >
                <Trash2 className="w-4 h-4 mr-2" /> Wipe Test Players…
            </Button>

            <Dialog open={open} onOpenChange={close}>
                <DialogContent className="bg-[#121212] border-[#FFD700]/40 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-[#FFD700]" />
                            {result ? 'Test Players Wiped' : 'Wipe Test Players'}
                        </DialogTitle>
                        {!result && (
                            <DialogDescription className="text-[#A0AAB2]">
                                Zero balances + delete transaction history of test accounts only. Real customers stay intact.
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    {result ? (
                        <div className="space-y-3 py-2" data-testid="wipe-tp-result-panel">
                            <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#39FF14]/30">
                                <p className="text-[#39FF14] font-bold mb-2">{result.message}</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[#A0AAB2]">
                                    <div className="flex justify-between"><span>Players zeroed</span><span className="text-white font-mono">{result.wiped_count}</span></div>
                                    <div className="flex justify-between"><span>Credits removed</span><span className="text-white font-mono">{fmt(result.credits_zeroed)}</span></div>
                                    <div className="flex justify-between"><span>Bonus removed</span><span className="text-white font-mono">{fmt(result.bonus_zeroed)}</span></div>
                                    <div className="flex justify-between"><span>Transactions</span><span className="text-white font-mono">{result.transactions_deleted}</span></div>
                                    {Object.entries(result.side_deleted || {}).map(([k, v]) => (
                                        <div key={k} className="flex justify-between"><span className="truncate pr-2">{k.replace(/_/g, ' ')}</span><span className="text-white font-mono">{v}</span></div>
                                    ))}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={() => close(false)} className="bg-[#FFD700] text-black font-bold w-full">Done</Button>
                            </DialogFooter>
                        </div>
                    ) : !preview ? (
                        <p className="text-[#A0AAB2] text-sm py-4">Loading candidate list…</p>
                    ) : (
                        <div className="space-y-4 py-2">
                            <div className="rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/5 p-3 space-y-1">
                                <p className="text-[12px] font-bold text-[#FFD700]">Will affect:</p>
                                <div className="grid grid-cols-2 gap-2 text-[12px]">
                                    <div className="flex justify-between"><span className="text-[#A0AAB2]">Players</span><span className="text-white font-mono font-black">{preview.count}</span></div>
                                    <div className="flex justify-between"><span className="text-[#A0AAB2]">Credits</span><span className="text-white font-mono font-black">{fmt(preview.total_credits)}</span></div>
                                    <div className="flex justify-between"><span className="text-[#A0AAB2]">Bonus</span><span className="text-white font-mono font-black">{fmt(preview.total_bonus)}</span></div>
                                    <div className="flex justify-between"><span className="text-[#A0AAB2]">Free play</span><span className="text-white font-mono font-black">{fmt(preview.total_free_play)}</span></div>
                                </div>
                            </div>

                            {preview.players.length > 0 && (
                                <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-2 max-h-40 overflow-y-auto" data-testid="wipe-tp-list">
                                    <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-1.5 px-1">First {Math.min(preview.players.length, 30)} of {preview.count}</p>
                                    {preview.players.slice(0, 30).map((p) => (
                                        <div key={p.id} className="flex justify-between gap-2 text-[11px] px-1 py-0.5 text-zinc-200">
                                            <span className="truncate flex-1">{p.name || '(no name)'} · <span className="text-[#A0AAB2]">{p.phone || p.email || '—'}</span></span>
                                            <span className="font-mono text-emerald-300 shrink-0">{fmt(p.credits)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div>
                                <Label className="text-white text-sm">
                                    Type <span className="font-mono bg-[#0a0a0a] px-2 py-0.5 rounded text-[#FFD700]">{TP_REQUIRED_PHRASE}</span> to confirm:
                                </Label>
                                <Input
                                    value={phrase}
                                    onChange={(e) => setPhrase(e.target.value)}
                                    placeholder={TP_REQUIRED_PHRASE}
                                    className="bg-[#0a0a0a] border-white/10 text-white font-mono mt-2"
                                    autoFocus autoComplete="off"
                                    data-testid="wipe-tp-confirm-input"
                                />
                            </div>

                            <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => close(false)} disabled={loading} className="border-white/20 text-white">Cancel</Button>
                                <Button
                                    onClick={doWipe}
                                    disabled={loading || !phraseValid || preview.count === 0}
                                    className="bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-bold disabled:opacity-50"
                                    data-testid="confirm-wipe-tp-btn"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {loading ? 'Wiping…' : `Wipe ${preview.count} now`}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export const DangerZone = () => {
    const [open, setOpen] = useState(false);
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const [keepUsers, setKeepUsers] = useState(true);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleWipe = async () => {
        if (confirmPhrase.trim() !== REQUIRED_PHRASE) {
            toast.error(`Type exactly: ${REQUIRED_PHRASE}`);
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/wipe-test-data`,
                { confirm_phrase: confirmPhrase.trim(), keep_users: keepUsers },
                { withCredentials: true }
            );
            setResult(data);
            toast.success('Test data wiped — ledger is fresh');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Wipe failed');
        }
        setLoading(false);
    };

    const handleClose = (o) => {
        if (loading) return;
        setOpen(o);
        if (!o) {
            setConfirmPhrase('');
            setKeepUsers(true);
            setResult(null);
        }
    };

    const phraseValid = confirmPhrase.trim() === REQUIRED_PHRASE;

    return (
        <div className="card-dark p-6 border-[#FF3B30]/30 bg-gradient-to-br from-[#FF3B30]/5 to-transparent" data-testid="danger-zone-panel">
            <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-[#FF3B30]" />
                Danger Zone
            </h3>
            <p className="text-xs text-[#A0AAB2] mb-4 max-w-xl">
                Use these controls only when you are certain. They cannot be undone.
            </p>

            <div className="rounded-lg border border-[#FF3B30]/30 bg-[#0a0a0a] p-4">
                <div className="flex items-start gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm">Wipe Test Data</p>
                        <p className="text-[11px] text-[#A0AAB2] mt-1">
                            Scrubs all transactions, sports bets, lottery tickets, lottery draws, free play history, audit logs, win-back fires, and notifications. Player accounts stay (credits reset to 0). Promo templates, schedules, win-back triggers, and cashout settings are preserved. Use this before going live with real customers.
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => setOpen(true)}
                    className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-bold w-full sm:w-auto"
                    data-testid="open-wipe-test-data-btn"
                >
                    <Trash2 className="w-4 h-4 mr-2" /> Wipe Test Data…
                </Button>
            </div>

            <WipeTestPlayersCard />

            <RestoreWipedPlayersCard />

            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="bg-[#121212] border-[#FF3B30]/40 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-[#FF3B30]" />
                            {result ? 'Wipe Complete' : 'Confirm Test Data Wipe'}
                        </DialogTitle>
                        {!result && (
                            <DialogDescription className="text-[#A0AAB2]">
                                This permanently deletes transactional data. There is no undo.
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    {result ? (
                        <div className="space-y-3 py-2" data-testid="wipe-result-panel">
                            <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#39FF14]/30">
                                <p className="text-[#39FF14] font-bold mb-2">{result.message}</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[#A0AAB2]">
                                    {Object.entries(result.counts || {}).map(([k, v]) => (
                                        <div key={k} className="flex justify-between">
                                            <span className="truncate pr-2">{k.replace(/_/g, ' ')}</span>
                                            <span className="text-white font-mono">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <p className="text-[10px] text-[#666]">
                                Tip: Reload the Reports / Player P&amp;L tabs to see the fresh state.
                            </p>
                            <DialogFooter>
                                <Button onClick={() => handleClose(false)}
                                    className="bg-[#FFD700] text-black font-bold w-full">
                                    Done
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4 py-2">
                            <div className="rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3">
                                <p className="text-[12px] text-[#FF3B30] font-bold mb-1">⚠ THIS WILL DELETE:</p>
                                <ul className="text-[11px] text-white space-y-0.5 list-disc list-inside">
                                    <li>All transactions (loads, cashouts, promos, etc.)</li>
                                    <li>All sports bets and settlement history</li>
                                    <li>All lottery tickets and draws</li>
                                    <li>All free play history</li>
                                    <li>All audit logs and win-back fire records</li>
                                    <li>All player notifications</li>
                                </ul>
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg bg-[#0a0a0a] border border-white/10">
                                <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-bold">Keep player accounts</p>
                                    <p className="text-[10px] text-[#A0AAB2]">If off, all non-admin user accounts are also deleted.</p>
                                </div>
                                <Switch checked={keepUsers} onCheckedChange={setKeepUsers}
                                    data-testid="wipe-keep-users-toggle" />
                            </div>

                            <div>
                                <Label className="text-white text-sm">
                                    Type <span className="font-mono bg-[#0a0a0a] px-2 py-0.5 rounded text-[#FF3B30]">{REQUIRED_PHRASE}</span> to confirm:
                                </Label>
                                <Input
                                    value={confirmPhrase}
                                    onChange={(e) => setConfirmPhrase(e.target.value)}
                                    placeholder={REQUIRED_PHRASE}
                                    className="bg-[#0a0a0a] border-white/10 text-white font-mono mt-2"
                                    autoFocus
                                    autoComplete="off"
                                    data-testid="wipe-confirm-phrase-input"
                                />
                            </div>

                            <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}
                                    className="border-white/20 text-white">Cancel</Button>
                                <Button onClick={handleWipe} disabled={loading || !phraseValid}
                                    className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-bold disabled:opacity-50"
                                    data-testid="confirm-wipe-btn">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {loading ? 'Wiping…' : 'Wipe Now'}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
